/**
 * Step list location grouping helpers (#999 Epic 3.2).
 */

import type {
  LocationTagGroupingInput,
  LocationUnitGroup,
} from './groupUnitsByLocationThenStatus';
import type { LocationTagDraft } from '../contexts/LocationTagsDraftContext';

export const LOCATION_UNTAGGED_GROUP_LABEL = 'Untagged';

export function draftTagsToGroupingInput(
  drafts: LocationTagDraft[],
): LocationTagGroupingInput[] {
  return drafts.map(draft => ({
    id: draft.clientId,
    name: draft.name,
  }));
}

/** Enable nested location → Existing/New when the request has location tags. */
export function shouldUseLocationListGrouping(
  tags: LocationTagGroupingInput[],
  _units: Array<{ locationTagId?: number | null }>,
): boolean {
  return tags.length > 0;
}

export function filterVisibleLocationGroups<T>(
  groups: LocationUnitGroup<T>[],
): LocationUnitGroup<T>[] {
  return groups.filter(
    group =>
      group.byStatus.existing.length > 0 || group.byStatus.new.length > 0,
  );
}

export function getLocationGroupKey<T>(group: LocationUnitGroup<T>): string {
  return group.isUntagged ? 'untagged' : String(group.locationTagId);
}

export function getLocationGroupTitle<T>(group: LocationUnitGroup<T>): string {
  if (group.isUntagged) {
    return LOCATION_UNTAGGED_GROUP_LABEL;
  }
  return group.locationTagName?.trim() || '—';
}

export function matchRepeatableUnitById<T extends { id?: number }>(
  left: T,
  right: T,
): boolean {
  return left.id != null && right.id != null && left.id === right.id;
}

/** Map drag indices within a location bucket to global EXISTING/NEW section indices. */
export function mapScopedSectionReorder<T>(
  bucketUnits: T[],
  globalSectionUnits: T[],
  matchUnit: (left: T, right: T) => boolean,
  onReorder: (fromGlobal: number, toGlobal: number) => void,
): (fromLocal: number, toLocal: number) => void {
  return (fromLocal: number, toLocal: number) => {
    const fromUnit = bucketUnits[fromLocal];
    const toUnit = bucketUnits[toLocal];
    if (!fromUnit || !toUnit) {
      return;
    }

    const fromGlobal = globalSectionUnits.findIndex(unit =>
      matchUnit(unit, fromUnit),
    );
    const toGlobal = globalSectionUnits.findIndex(unit =>
      matchUnit(unit, toUnit),
    );

    if (fromGlobal < 0 || toGlobal < 0) {
      return;
    }

    onReorder(fromGlobal, toGlobal);
  };
}
